import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import apiClient from "../api/apiClient";

export const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

const getUserMobileNo = (userInfo) =>
  userInfo?.mobileNo ||
  userInfo?.mobile ||
  userInfo?.phone ||
  userInfo?.phoneNumber ||
  userInfo?.mobile_no ||
  "";

const getStoredMobileNo = () => {
  const directMobile =
    localStorage.getItem("mobileNo") || localStorage.getItem("lastLoginMobileNo");

  if (directMobile) return directMobile;

  const storedProfile = localStorage.getItem("userProfile");
  if (!storedProfile) return "";

  try {
    return getUserMobileNo(JSON.parse(storedProfile));
  } catch {
    return "";
  }
};

export const AppProvider = ({ children }) => {
  // ---------- USER STATE ----------
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("userProfile");
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      localStorage.removeItem("userProfile");
      return null;
    }
  });

  const [loading, setLoading] = useState(true);

  // ---------- AUTH STATE ----------
  const [mobileNo, setMobileNo] = useState(
    () => getStoredMobileNo()
  );
  const [userPin, setUserPin] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const mobileNoRef = useRef(mobileNo);
  const userRef = useRef(user);

  useEffect(() => {
    mobileNoRef.current = mobileNo;
  }, [mobileNo]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // ---------- MOBILE SYNC ----------
  useEffect(() => {
    if (mobileNo) {
      localStorage.setItem("mobileNo", mobileNo);
      localStorage.setItem("lastLoginMobileNo", mobileNo);
    } else {
      localStorage.removeItem("mobileNo");
    }
  }, [mobileNo]);

  // ---------- WALLET UPDATE ----------
  const updateWalletBalance = useCallback((newBalance) => {
    setUser((prev) => {
      if (!prev) return prev;

      const updated = {
        ...prev,
        walletBalance: String(newBalance),
      };

      localStorage.setItem("userProfile", JSON.stringify(updated));
      localStorage.setItem("walletBalance", String(newBalance));

      return updated;
    });
  }, []);

  // ---------- LOGOUT ----------
  const resetUserContext = useCallback(() => {
    console.log("LOGOUT: clearing user session");
    const lastMobileNo =
      mobileNoRef.current ||
      getUserMobileNo(userRef.current) ||
      getStoredMobileNo();

    setUser(null);
    setMobileNo(lastMobileNo);
    setUserPin("");
    setOtp("");
    setPassword("");

    localStorage.removeItem("accessToken");
    localStorage.removeItem("registerId");
    localStorage.removeItem("walletBalance");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("feeSettings");

    if (lastMobileNo) {
      localStorage.setItem("mobileNo", lastMobileNo);
      localStorage.setItem("lastLoginMobileNo", lastMobileNo);
    }
  }, []);

  // ---------- FETCH PROFILE ----------
  const fetchUserProfile = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    const registerId = localStorage.getItem("registerId");

    if (!token || !registerId) {
      setUser(null);
      setLoading(false);
      return;
    }

    // ---- DEV / DUMMY TOKEN BYPASS ----
    if (token === "dummy_token") {
      const stored = localStorage.getItem("userProfile");
      if (stored) {
        try {
          setUser(JSON.parse(stored));
        } catch {
          localStorage.removeItem("userProfile");
        }
      }
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const res = await apiClient.post(
        "/user-details-by-register-id",
        { registerId }
      );

      if (res.data?.status === true) {
        const userInfo = res.data.info;

        setUser(userInfo);

        localStorage.setItem("userProfile", JSON.stringify(userInfo));
        localStorage.setItem("walletBalance", userInfo.walletBalance);
        const profileMobileNo = getUserMobileNo(userInfo);
        if (profileMobileNo) {
          setMobileNo(profileMobileNo);
          localStorage.setItem("mobileNo", profileMobileNo);
          localStorage.setItem("lastLoginMobileNo", profileMobileNo);
        }
      } else {
        console.error("Profile fetch failed:", res.data?.msg);
        resetUserContext();
      }
    } catch (err) {
      console.error("Profile API error:", err);
    } finally {
      setLoading(false);
    }
  }, [resetUserContext]);

  // ---------- INIT ----------
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // ---------- CONTEXT VALUE ----------
  const value = {
    user,
    setUser,
    loading,

    mobileNo,
    setMobileNo,

    userPin,
    setUserPin,

    otp,
    setOtp,

    password,
    setPassword,

    updateWalletBalance,
    resetUserContext,
    refetchUser: fetchUserProfile,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
